import { useEffect, useRef, useState } from "react";
import { Play, ArrowCounterClockwise, GameController } from "@phosphor-icons/react";
import { gameWinTexts, gameLoseTexts, gameDrawTexts } from "@/data/funzone";

const LINES = [
  [0, 1, 2], [3, 4, 5], [6, 7, 8],
  [0, 3, 6], [1, 4, 7], [2, 5, 8],
  [0, 4, 8], [2, 4, 6],
];

const winnerOf = (b) => {
  for (const [a, c, d] of LINES) {
    if (b[a] && b[a] === b[c] && b[a] === b[d]) return b[a];
  }
  return null;
};

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const findLineMove = (b, mark) => {
  for (const line of LINES) {
    const vals = line.map((i) => b[i]);
    if (vals.filter((v) => v === mark).length === 2 && vals.includes(null)) {
      return line[vals.indexOf(null)];
    }
  }
  return -1;
};

const StoneIcon = ({ className = "w-7 h-7" }) => (
  <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
    <path d="M9 24 L5 16 L10 8 L20 6 L27 12 L26 21 L18 26 Z" fill="#94a3b8" stroke="#475569" strokeWidth="2" strokeLinejoin="round" />
    <path d="M10 8 L16 14 L27 12 M16 14 L18 26 M16 14 L5 16" stroke="#64748b" strokeWidth="1.5" fill="none" />
  </svg>
);

const GlassIcon = ({ className = "w-7 h-7" }) => (
  <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
    <path d="M6 10 Q16 6 26 10 L28 22 Q16 26 4 22 Z" fill="#bae6fd" stroke="#0369a1" strokeWidth="2" strokeLinejoin="round" />
    <path d="M10 12 L14 21" stroke="#7dd3fc" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

export const StoneVsGlassGame = () => {
  const [phase, setPhase] = useState("idle");
  const [board, setBoard] = useState(Array(9).fill(null));
  const [playerTurn, setPlayerTurn] = useState(true);
  const [result, setResult] = useState("");
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const start = () => {
    setBoard(Array(9).fill(null));
    setResult("");
    setPlayerTurn(true);
    setPhase("playing");
  };

  const finish = (b) => {
    const w = winnerOf(b);
    if (w === "S") { setPhase("won"); setResult(pick(gameWinTexts)); return true; }
    if (w === "G") { setPhase("lost"); setResult(pick(gameLoseTexts)); return true; }
    if (b.every(Boolean)) { setPhase("draw"); setResult(pick(gameDrawTexts)); return true; }
    return false;
  };

  const computerMove = (b) => {
    const free = b.map((v, i) => (v ? -1 : i)).filter((i) => i >= 0);
    if (!free.length) return;
    let move = findLineMove(b, "G");
    if (move === -1 && Math.random() < 0.75) move = findLineMove(b, "S");
    if (move === -1 && b[4] === null && Math.random() < 0.6) move = 4;
    if (move === -1) move = pick(free);
    const next = [...b];
    next[move] = "G";
    setBoard(next);
    if (!finish(next)) setPlayerTurn(true);
  };

  const handleCell = (i) => {
    if (phase !== "playing" || !playerTurn || board[i]) return;
    const next = [...board];
    next[i] = "S";
    setBoard(next);
    setPlayerTurn(false);
    if (!finish(next)) {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => computerMove(next), 450);
    }
  };

  const over = phase === "won" || phase === "lost" || phase === "draw";

  return (
    <div
      className="rounded-3xl bg-amber-50 border border-amber-100 shadow-sm p-6 md:p-8 flex flex-col items-center text-center transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl"
      data-testid="game-card"
    >
      <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center">
        <GameController size={26} weight="duotone" />
      </div>
      <h3 className="mt-4 text-xl font-bold text-slate-900">Stein vs. Scheibe</h3>
      <p className="mt-1 text-sm text-amber-700 font-medium">Tic-Tac-Toe für schnelle Köpfe.</p>

      <div className="mt-5 w-full max-w-[240px] rounded-t-[42px] rounded-b-2xl border-4 border-slate-800 bg-gradient-to-b from-sky-200 to-sky-50 p-2.5" data-testid="game-board">
        {phase === "idle" ? (
          <div className="aspect-square flex flex-col items-center justify-center gap-2 text-slate-600">
            <div className="flex items-center gap-2">
              <StoneIcon className="w-9 h-9" />
              <span className="font-bold text-slate-700">vs.</span>
              <GlassIcon className="w-9 h-9" />
            </div>
            <p className="text-xs font-medium px-4">Du spielst den Stein — die Scheibe wehrt sich.</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-1.5" role="grid" aria-label="Tic-Tac-Toe Spielfeld">
            {board.map((cell, i) => (
              <button
                key={i}
                onClick={() => handleCell(i)}
                disabled={!!cell || over || !playerTurn}
                aria-label={`Feld ${i + 1}${cell ? `, belegt mit ${cell === "S" ? "Stein" : "Scheibe"}` : ", frei"}`}
                className="aspect-square rounded-lg bg-white/75 hover:bg-white disabled:hover:bg-white/75 flex items-center justify-center transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-orange-400"
                data-testid={`game-cell-${i}`}
              >
                {cell === "S" && <StoneIcon />}
                {cell === "G" && <GlassIcon />}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 min-h-[3.5rem] flex items-center justify-center">
        {phase === "playing" && (
          <p className="text-sm font-semibold text-slate-600" aria-live="polite">
            {playerTurn ? "Du bist dran — setze deinen Stein." : "Die Scheibe überlegt …"}
          </p>
        )}
        {over && (
          <p className="fz-fade-in text-base font-bold text-slate-800" data-testid="game-result" aria-live="polite">
            {result}
          </p>
        )}
      </div>

      <button onClick={start} className="mt-auto pt-2 w-full" data-testid={phase === "idle" ? "game-start-button" : "game-restart-button"}>
        <span className="inline-flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-orange-500 hover:bg-orange-600 active:scale-[0.98] text-white font-semibold transition-[background-color,transform] duration-200 focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2">
          {phase === "idle" ? <Play size={18} weight="fill" /> : <ArrowCounterClockwise size={18} weight="bold" />}
          {phase === "idle" ? "Spiel starten" : over ? "Nochmal spielen" : "Neu starten"}
        </span>
      </button>
    </div>
  );
};
